// Customer Display wire protocol. Framework-independent and long-lived: shared
// by the Vue 2 publisher tabs and the Vue 3 display window. Both sides pass
// every view through sanitizeView(), which is the ONLY place that decides which
// fields may reach the customer-facing window (explicit allow-list).

export const PROTOCOL_VERSION = 1;
export const CHANNEL_NAME = 'permtang.customer-display';
export const DISPLAY_WINDOW_NAME = 'permtang-customer-display';

export const MESSAGE = {
  STATE: 'state', // publisher tab -> display: what to show
  HELLO: 'hello', // display -> tabs: (re)send your current state
  ALIVE: 'display-alive', // display -> tabs: heartbeat
  BYE: 'display-bye', // display -> tabs: window is closing
  FOCUS: 'focus', // tab -> display: please bring yourself to the front
  CLOSE: 'close', // tab -> display: please close yourself
  // display -> tabs: a direct submit_rating succeeded for this session id (the
  // display never relays the submission itself - only this small
  // acknowledgement - so the owning publisher's own state stays correct
  // across a HELLO replay instead of resending stale, already-rated controls).
  RATING_COMPLETED: 'rating-completed',
  // display -> tabs: the post-rating screen's own timer has elapsed for this
  // session id, so the owning publisher can go fully idle too - otherwise a
  // display refresh/reopen would replay the finished flow from the start.
  RATING_FLOW_DONE: 'rating-flow-done',
  // display -> tabs: the pre-payment inline session for this id has expired
  // or was rejected by the backend as invalid, as discovered by the
  // display's own live submit_rating attempt or its own expiry watch - the
  // ACTIVE document itself stays visible (unlike RATING_FLOW_DONE, which is
  // for the full post-payment flow finishing), only the matching rating
  // session is marked no longer ratable so a replay does not resend dead
  // controls.
  RATING_UNAVAILABLE: 'rating-unavailable'
};

export const VIEW = { IDLE: 'idle', ACTIVE: 'active', THANK_YOU: 'thank-you', RATING: 'rating' };

export const DOC_TYPES = ['quotation', 'invoice', 'receipt', 'debit-note', 'credit-note'];

const MAX_ITEMS = 60;

function str(value, max) {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, max);
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function imageUrl(value) {
  const url = str(value, 2048 * 8);
  if (/^https?:\/\/\S+$/i.test(url) && url.length <= 2048) return url;
  if (/^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/=]+$/.test(url)) return url;
  return '';
}

const NAME_TITLES = [
  'นาย', 'นาง', 'นางสาว', 'น.ส.', 'นส.', 'ด.ช.', 'ด.ญ.', 'เด็กชาย', 'เด็กหญิง', 'คุณ',
  'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'miss'
];
const SURNAME_VISIBLE_CHARS = 4;
const SURNAME_MASK = 'xxx';

// Owner name for the customer-facing screen: the given name is shown in full,
// the surname (last word) keeps only its first 4 characters followed by "xxx".
// A single word, or "title + one word", has no surname to hide. Idempotent, so
// running it again on an already masked name (the display re-sanitizes every
// message) changes nothing; a raw surname always gets masked.
export function maskOwnerName(value) {
  const words = String(value === null || value === undefined ? '' : value)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length < 2) return words.join(' ');
  if (words.length === 2 && NAME_TITLES.indexOf(words[0].toLowerCase()) !== -1) return words.join(' ');
  const surname = Array.from(words[words.length - 1]);
  const alreadyMasked =
    surname.length <= SURNAME_VISIBLE_CHARS + SURNAME_MASK.length && surname.slice(-SURNAME_MASK.length).join('') === SURNAME_MASK;
  if (!alreadyMasked) words[words.length - 1] = surname.slice(0, SURNAME_VISIBLE_CHARS).join('') + SURNAME_MASK;
  return words.join(' ');
}

const PHONE_MASK = 'xxx';

// Hides the last 3 digits of a phone number: "0812345678" -> "0812345xxx".
// Idempotent, so an already masked number passes through unchanged.
export function maskPhoneTail(value) {
  const raw = String(value === null || value === undefined ? '' : value).trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return ''; // no number: nothing to hide, and no lone "xxx" to show
  if (/x{3}$/i.test(raw)) return digits + PHONE_MASK;
  return (digits.length > PHONE_MASK.length ? digits.slice(0, -PHONE_MASK.length) : '') + PHONE_MASK;
}

// Digits only, except a trailing "xxx" left by maskPhoneTail().
function sanitizePhone(value) {
  const raw = String(value === null || value === undefined ? '' : value).trim();
  const digits = raw.replace(/\D/g, '').slice(0, 15);
  return /x{3}$/i.test(raw) ? digits + PHONE_MASK : digits;
}

const DEFAULT_POST_RATING_SECONDS = 60;

function sanitizeBranch(branch) {
  if (!branch || typeof branch !== 'object') return null;
  const seconds = num(branch.postRatingDisplaySeconds);
  return {
    name: str(branch.name, 120),
    logoUrl: imageUrl(branch.logoUrl),
    // Branch-level Service Rating post-rating content (title/content/image) -
    // static and meant to be read by the customer, so it is allow-listed here
    // like name/logoUrl rather than needing a session-scoped field.
    postRatingTitle: str(branch.postRatingTitle, 120),
    postRatingContent: str(branch.postRatingContent, 500),
    postRatingImageUrl: imageUrl(branch.postRatingImageUrl),
    postRatingDisplaySeconds: seconds > 0 ? seconds : DEFAULT_POST_RATING_SECONDS
  };
}

function sanitizeQr(qr) {
  if (!qr || typeof qr !== 'object') return null;
  if (qr.kind === 'promptpay') {
    const payload = str(qr.payload, 512);
    // Printable text only. Thai shop names (UTF-8) are allowed; control, format and
    // unassigned characters are not.
    return /^[^\p{Cc}\p{Cf}\p{Cs}\p{Co}\p{Cn}\p{Zl}\p{Zp}]+$/u.test(payload) ? { kind: 'promptpay', payload } : null;
  }
  if (qr.kind === 'image') {
    const url = imageUrl(qr.url);
    return url ? { kind: 'image', url } : null;
  }
  return null;
}

function sanitizePayment(payment) {
  if (!payment || typeof payment !== 'object') return null;
  const qr = sanitizeQr(payment.qr);
  if (!qr) return null;
  return { qr, accountName: str(payment.accountName, 120), bankName: str(payment.bankName, 120) };
}

export const PAYMENT_CHANNELS = ['cash', 'transfer', 'credit', 'cheque'];

// What the cashier is keying in at the Pay page: the chosen channel plus,
// for cash, the amount handed over and what is owed back or still short. The
// numbers are a straight mirror of the figures already on the cashier's own
// screen (net_price vs. amount received) - this allow-list does not recompute
// them, only limits what crosses to the customer-facing window.
function sanitizeSettlement(settlement) {
  if (!settlement || typeof settlement !== 'object') return null;
  const channel = PAYMENT_CHANNELS.indexOf(settlement.channel) === -1 ? '' : settlement.channel;
  if (!channel) return null;
  return {
    channel,
    // 'refund' = the shop is paying the customer back (debit note); anything
    // else is the normal direction, the shop collecting from the customer.
    direction: settlement.direction === 'refund' ? 'refund' : 'collect',
    received: num(settlement.received),
    change: num(settlement.change),
    overdue: num(settlement.overdue),
    reference: str(settlement.reference, 60)
  };
}

const RATING_SESSION_STATUSES = ['waiting', 'rated', 'timeout', 'cancelled_by_new_document', 'closed_without_payment'];
const RATING_VALUES = [1, 2, 3];

// What a tab learned from evaluation_session/get_or_create. Carried on ACTIVE
// (so a future rating widget can appear before payment) and on RATING (the
// full-screen prompt after payment, while not yet rated) - never anything
// else about the session (document_id, branch_id, ...), and the token is the
// only thing that ever authorizes a rating submission, so it travels through
// this one allow-listed field rather than any ambient page/session identity.
// rating (1/2/3, the customer's own choice) is carried too - not sensitive,
// and needed so the inline widget can show "already rated" with the right
// face on a fresh load, not only right after a live submit.
function sanitizeRatingSession(ratingSession) {
  if (!ratingSession || typeof ratingSession !== 'object') return null;
  const id = str(ratingSession.id, 64);
  const ratingToken = str(ratingSession.ratingToken, 128);
  if (!id || !ratingToken || RATING_SESSION_STATUSES.indexOf(ratingSession.status) === -1) return null;
  const rating = Number(ratingSession.rating);
  return {
    id,
    ratingToken,
    status: ratingSession.status,
    expiresAt: str(ratingSession.expiresAt, 32),
    rating: RATING_VALUES.indexOf(rating) === -1 ? null : rating
  };
}

// The only state that may become the full-screen rating prompt. Blank or
// malformed expiry is never an unlimited window; equality is allowed because
// the backend's atomic submit claim also uses expires_at >= now.
export function isRatableRatingSession(ratingSession, now = Date.now()) {
  if (!ratingSession || ratingSession.status !== 'waiting' || !ratingSession.expiresAt) return false;
  const expiresAt = Date.parse(ratingSession.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt >= now;
}

// Pre-payment inline rating (on the still-ACTIVE document) normally has no
// deadline at all - expires_at is only ever set once payment succeeds (see
// the backend's mark_evaluation_session_paid), so a blank expiry is always
// ratable here, unlike isRatableRatingSession. But a session that started
// waiting pre-payment can still be reused after payment (the same session
// row), so if expiresAt IS present it must be parseable and not yet passed -
// otherwise a document left open past its session's own deadline would keep
// offering a tap that the backend's own atomic claim would then refuse
// anyway. Backend remains the final authority regardless.
export function isInlineRatingAvailable(ratingSession, now = Date.now()) {
  if (!ratingSession || ratingSession.status !== 'waiting') return false;
  if (!ratingSession.expiresAt) return true;
  const expiresAt = Date.parse(ratingSession.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt >= now;
}

function sanitizeDoc(doc) {
  const source = doc && typeof doc === 'object' ? doc : {};
  const plate = source.plate && typeof source.plate === 'object' ? source.plate : {};
  const totals = source.totals && typeof source.totals === 'object' ? source.totals : {};
  const items = Array.isArray(source.items) ? source.items.slice(0, MAX_ITEMS) : [];
  return {
    docType: DOC_TYPES.indexOf(source.docType) === -1 ? 'quotation' : source.docType,
    plate: {
      characters: str(plate.characters, 12),
      number: str(plate.number, 12),
      province: str(plate.province, 60)
    },
    // false = the live document differs from the last save (or nothing is saved
    // yet). Informational: the payment QR is what waits for a save. Defaults to
    // true for senders that do not know the flag.
    saved: source.saved !== false,
    // Vehicle type label (e.g. "รย.12 รถจักรยานยนต์ส่วนบุคคล"). Owner name's
    // surname is shown in full while staff are still typing it (same rule as
    // phone above: the customer can verify it before it is saved) and masked
    // only once the document has actually been saved (saved !== false, same
    // flag/default as the `saved` field itself).
    carType: str(source.carType, 120),
    ownerName: source.saved !== false ? maskOwnerName(str(source.ownerName, 120)) : str(source.ownerName, 120),
    phone: sanitizePhone(source.phone),
    items: items
      .filter((item) => item && typeof item === 'object')
      .map((item) => ({
        name: str(item.name, 200),
        price: num(item.price),
        discount: num(item.discount)
      })),
    totals: {
      subtotal: num(totals.subtotal),
      discount: num(totals.discount),
      vat: num(totals.vat),
      net: num(totals.net)
    }
  };
}

// Returns a brand new object containing only allow-listed, type-coerced
// fields. Unknown kinds become idle.
export function sanitizeView(view) {
  const source = view && typeof view === 'object' ? view : {};
  const branch = sanitizeBranch(source.branch);
  if (source.kind === VIEW.THANK_YOU) {
    // Carried only when the session was already rated before payment (the
    // publisher only ever attaches it in that case - see paymentSucceeded):
    // tells the display to show the configured post-rating content with its
    // own timer instead of the plain indefinite thank-you. Anything else
    // (timeout, closed, cancelled, unknown, no session, out-of-scope
    // document) keeps that plain fail-closed screen exactly as before.
    const ratingSession = sanitizeRatingSession(source.ratingSession);
    return {
      kind: VIEW.THANK_YOU,
      branch,
      ratingSession: ratingSession && ratingSession.status === 'rated' ? ratingSession : null,
      settlement: sanitizeSettlement(source.settlement)
    };
  }
  if (source.kind === VIEW.RATING) {
    const ratingSession = sanitizeRatingSession(source.ratingSession);
    // Fail closed to the established post-payment screen. A malformed/expired
    // RATING message must never become an idle screen or an unlimited prompt.
    if (!ratingSession || !isRatableRatingSession(ratingSession)) {
      return { kind: VIEW.THANK_YOU, branch, settlement: sanitizeSettlement(source.settlement) };
    }
    return { kind: VIEW.RATING, branch, ratingSession, settlement: sanitizeSettlement(source.settlement) };
  }
  if (source.kind === VIEW.ACTIVE) {
    return {
      kind: VIEW.ACTIVE,
      branch,
      doc: sanitizeDoc(source.doc),
      payment: sanitizePayment(source.payment),
      settlement: sanitizeSettlement(source.settlement),
      // Absent/null until a document's first save resolves (see documentMixin's
      // async fetch) or the branch does not have Service Rating enabled.
      ratingSession: sanitizeRatingSession(source.ratingSession)
    };
  }
  return { kind: VIEW.IDLE, branch };
}

export function createStateMessage({ sender, epoch, seq, view }) {
  return {
    v: PROTOCOL_VERSION,
    type: MESSAGE.STATE,
    sender: str(sender, 64),
    epoch: num(epoch),
    seq: num(seq),
    view: sanitizeView(view)
  };
}

export function createSignal(type, extra) {
  return Object.assign({ v: PROTOCOL_VERSION, type }, extra || {});
}

// Validates an inbound message; returns a normalized copy or null. Messages of
// another protocol version are ignored so old tabs and new displays (or vice
// versa) cannot misinterpret each other after a deploy.
export function parseMessage(raw) {
  if (!raw || typeof raw !== 'object' || raw.v !== PROTOCOL_VERSION) return null;
  const known = Object.keys(MESSAGE).some((key) => MESSAGE[key] === raw.type);
  if (!known) return null;
  if (raw.type === MESSAGE.RATING_COMPLETED) {
    const sessionId = str(raw.sessionId, 64);
    if (!sessionId) return null;
    const rating = Number(raw.rating);
    return { v: raw.v, type: raw.type, sessionId, rating: RATING_VALUES.indexOf(rating) === -1 ? null : rating };
  }
  if (raw.type === MESSAGE.RATING_FLOW_DONE || raw.type === MESSAGE.RATING_UNAVAILABLE) {
    const sessionId = str(raw.sessionId, 64);
    if (!sessionId) return null;
    return { v: raw.v, type: raw.type, sessionId };
  }
  if (raw.type !== MESSAGE.STATE) return { v: raw.v, type: raw.type };
  const sender = str(raw.sender, 64);
  if (!sender) return null;
  return createStateMessage({ sender, epoch: raw.epoch, seq: raw.seq, view: raw.view });
}

export function createInitialDisplayState() {
  return { view: { kind: VIEW.IDLE, branch: null }, owner: null };
}

// Pure reducer used by the display window. Ownership rule:
//  - a message from a tab with a NEWER epoch (it became the active tab later)
//    takes over the display;
//  - within one epoch only the owner is accepted, with increasing seq;
//  - everything else (stale/inactive tabs) is dropped.
// THANK_YOU and RATING are both sticky: only an ACTIVE view replaces either;
// idle/release messages never clear them. Both are payment-completion events
// (see paymentMixin's customerDisplayPaymentSucceeded), so both get the same
// "cannot take over another tab's newer document" protection.
const STICKY_VIEWS = [VIEW.THANK_YOU, VIEW.RATING];

export function reduceDisplayState(state, message) {
  if (!message || message.type !== MESSAGE.STATE) return state;
  const owner = state.owner;
  const accepted =
    !owner ||
    message.epoch > owner.epoch ||
    (message.epoch === owner.epoch && message.sender === owner.sender && message.seq > owner.seq);
  if (!accepted) return state;

  const nextOwner = { sender: message.sender, epoch: message.epoch, seq: message.seq };
  const incoming = message.view;
  // Payment completion is an event for the document that already owns the
  // display. It cannot take over another tab's newer document by itself.
  if (STICKY_VIEWS.indexOf(incoming.kind) !== -1 && owner && message.sender !== owner.sender) return state;
  if (STICKY_VIEWS.indexOf(state.view.kind) !== -1 && incoming.kind === VIEW.IDLE) {
    // Keep everything already on the thank-you/rating screen (including a
    // payment settlement) - an idle/release message only updates the branch.
    const branch = incoming.branch || state.view.branch;
    return { view: Object.assign({}, state.view, { branch }), owner: nextOwner };
  }
  return { view: incoming, owner: nextOwner };
}
